import React, { useState, useEffect, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Send, Reply, Smile, Search, Check, CheckCheck, ChevronLeft,
  MoreVertical, User, Clock, X, Forward, Phone, Video,
  Info, Image, ChevronDown, Paperclip, Mic, Camera
} from "lucide-react";
import data from "@emoji-mart/data";
import Picker from "@emoji-mart/react";
import { format, isToday, isYesterday, isSameDay } from "date-fns";
import { useChat } from "../contexts/ChatContext";
import { toast } from "react-hot-toast";
import linkify from 'linkify-it';
import tlds from 'tlds';
const linkifyInstance = linkify().tlds(tlds);
// Typing debounce delay
const TYPING_DELAY = 1000;

// Helper to format file size
const formatFileSize = (bytes) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

// Online Status Indicator
const OnlineStatus = ({ isOnline }) => (
  <div className="absolute bottom-0 right-0 transform translate-x-1/4 translate-y-1/4">
    <div
      className={`
      w-3 h-3 rounded-full border-2 border-white
      ${isOnline ? "bg-green-500" : "bg-gray-400"}
    `}
    />
  </div>
);

// Typing Indicator
const TypingIndicator = ({ show }) => {
  if (!show) return null;

  return (
    <div className="flex items-center space-x-2 p-2 text-sm text-gray-500">
      <div className="flex space-x-1">
        <div
          className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
          style={{ animationDelay: "0ms" }}
        />
        <div
          className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
          style={{ animationDelay: "150ms" }}
        />
        <div
          className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
          style={{ animationDelay: "300ms" }}
        />
      </div>
      <span>typing...</span>
    </div>
  );
};

// Date Separator
const DateSeparator = ({ date }) => (
  <div className="flex items-center justify-center my-6">
    <div className="px-4 py-1.5 rounded-full bg-gray-100 text-xs font-medium text-gray-600">
      {isToday(date)
        ? "Today"
        : isYesterday(date)
        ? "Yesterday"
        : format(date, "MMMM d, yyyy")}
    </div>
  </div>
);
const ChatHeader = ({ connection, onBack, isOnline }) => {
  return (
    <div className="bg-white border-b border-gray-100">
      <div className="px-4 py-3 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={onBack}
            className="md:hidden p-2 hover:bg-gray-100 rounded-full transition-all"
          >
            <ChevronLeft className="h-5 w-5 text-gray-500" />
          </button>

          <div className="flex items-center space-x-3">
            <div className="relative">
              {connection.otherParty.avatar ? (
                <img
                  src={connection.otherParty.avatar}
                  alt={connection.otherParty.firstName}
                  className="h-12 w-12 rounded-full object-cover ring-2 ring-white"
                />
              ) : (
                <div className="h-12 w-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center ring-2 ring-white">
                  <span className="text-lg font-medium text-white">
                    {connection.otherParty.firstName[0]}
                  </span>
                </div>
              )}
              <OnlineStatus isOnline={isOnline} />
            </div>

            <div>
              <h3 className="font-semibold text-gray-900">
                {connection.otherParty.firstName} {connection.otherParty.lastName}
              </h3>
              <p className="text-sm text-gray-500 flex items-center space-x-2">
                <span>{connection.otherParty.department}</span>
                {isOnline && (
                  <span className="flex items-center text-green-500">
                    <span className="h-1.5 w-1.5 rounded-full bg-green-500 mr-1" />
                    Online
                  </span>
                )}
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center space-x-1">
          <button className="p-2 hover:bg-gray-100 rounded-full transition-all">
            <Search className="h-5 w-5 text-gray-500" />
          </button>
          <button className="hidden md:block p-2 hover:bg-gray-100 rounded-full transition-all">
            <Phone className="h-5 w-5 text-gray-500" />
          </button>
          <button className="hidden md:block p-2 hover:bg-gray-100 rounded-full transition-all">
            <Video className="h-5 w-5 text-gray-500" />
          </button>
          <button className="p-2 hover:bg-gray-100 rounded-full transition-all">
            <Info className="h-5 w-5 text-gray-500" />
          </button>
        </div>
      </div>
    </div>
  );
};

const MessageInput = ({
  value,
  onChange,
  onSend,
  replyTo,
  onCancelReply,
  showEmojiPicker,
  onToggleEmojiPicker,
  onEmojiSelect,
  onAttachmentClick,
  isRecording,
  onRecordStart,
  onRecordStop,
}) => (
  <div className="bg-white border-t border-gray-100 px-4 py-3">
    {replyTo && (
      <div className="mb-3 bg-gray-50 rounded-lg p-3 flex items-start justify-between">
        <div className="flex items-start space-x-3">
          <Reply className="h-5 w-5 text-blue-500 mt-1" />
          <div>
            <p className="text-sm font-medium text-gray-900">
              Reply to {replyTo.sender.firstName}
            </p>
            <p className="text-sm text-gray-500 truncate">{replyTo.content}</p>
          </div>
        </div>
        <button
          onClick={onCancelReply}
          className="text-gray-400 hover:text-gray-600"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
    )}

    <div className="flex items-end space-x-2">
      <div className="flex-1 relative">
        <textarea
          value={value}
          onChange={onChange}
          placeholder="Type a message..."
          className="w-full p-4 pr-20 bg-gray-50 rounded-2xl resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all max-h-32"
          rows={1}
          onKeyPress={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              onSend();
            }
          }}
        />
        <div className="absolute right-2 bottom-2 flex items-center space-x-1">
          <button
            onClick={onAttachmentClick}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-all"
          >
            <Paperclip className="h-5 w-5" />
          </button>
          <button
            onClick={onToggleEmojiPicker}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-all"
          >
            <Smile className="h-5 w-5" />
          </button>
          <button
            onClick={onRecordStart}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-all"
          >
            <Mic className="h-5 w-5" />
          </button>
        </div>

        {showEmojiPicker && (
          <div className="absolute bottom-full right-0 mb-2">
            <div className="shadow-lg rounded-lg overflow-hidden">
              <Picker data={data} onEmojiSelect={onEmojiSelect} theme="light" />
            </div>
          </div>
        )}
      </div>

      <button
        onClick={onSend}
        disabled={!value.trim() && !isRecording}
        className={`
          p-4 rounded-full transition-all
          ${
            value.trim() || isRecording
              ? "bg-blue-600 hover:bg-blue-700 text-white"
              : "bg-gray-100 text-gray-400 cursor-not-allowed"
          }
        `}
      >
        <Send className="h-5 w-5" />
      </button>
    </div>
  </div>
);
const MessageList = ({ messages, onReply, onForward }) => {
  const messagesEndRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);
  const [prevScrollHeight, setPrevScrollHeight] = useState(0);
  
  // Handle scroll behavior
  useEffect(() => {
    const handleScroll = () => {
      if (!scrollContainerRef.current) return;
      
      const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
      setShouldAutoScroll(isNearBottom);
      setPrevScrollHeight(scrollHeight);
    };

    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
      return () => scrollContainer.removeEventListener('scroll', handleScroll);
    }
  }, []);

  // Handle auto-scrolling
  useEffect(() => {
    if (!scrollContainerRef.current || !shouldAutoScroll) return;
    
    const scrollContainer = scrollContainerRef.current;
    const newScrollHeight = scrollContainer.scrollHeight;
    
    // Only scroll if content has been added
    if (newScrollHeight > prevScrollHeight) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, shouldAutoScroll, prevScrollHeight]);

  // Group messages by date and sender
  const groupedMessages = useMemo(() => {
    const groups = [];
    let currentGroup = [];
    let lastDate = null;
    let lastSender = null;

    messages.forEach((message, index) => {
      const messageDate = new Date(message.createdAt);
      const isInSameDay = lastDate && isSameDay(lastDate, messageDate);
      const isSameSender = lastSender === message.sender._id;
      
      if (!isInSameDay || !isSameSender) {
        if (currentGroup.length > 0) {
          groups.push(currentGroup);
        }
        currentGroup = [message];
      } else {
        currentGroup.push(message);
      }

      lastDate = messageDate;
      lastSender = message.sender._id;

      if (index === messages.length - 1 && currentGroup.length > 0) {
        groups.push(currentGroup);
      }
    });

    return groups;
  }, [messages]);

  return (
    <div 
      ref={scrollContainerRef}
      className="flex-1 overflow-y-auto bg-[#F8FAFC] scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent"
    >
      <div className="min-h-full p-4 space-y-6">
        {groupedMessages.map((group, groupIndex) => {
          const firstMessageInGroup = group[0];
          const showDateSeparator = 
            groupIndex === 0 || 
            !isSameDay(
              new Date(firstMessageInGroup.createdAt),
              new Date(groupedMessages[groupIndex - 1][0].createdAt)
            );

          return (
            <div key={`group-${groupIndex}`} className="space-y-1">
              {showDateSeparator && (
                <DateSeparator date={new Date(firstMessageInGroup.createdAt)} />
              )}
              
              {group.map((message, messageIndex) => (
                <ChatMessage
                  key={message._id}
                  message={message}
                  onReply={onReply}
                  onForward={onForward}
                  isOwnMessage={message.sender._id === localStorage.getItem("currentAlumniId")}
                  showAvatar={messageIndex === group.length - 1}
                  isFirstInGroup={messageIndex === 0}
                  isLastInGroup={messageIndex === group.length - 1}
                />
              ))}
            </div>
          );
        })}
        
        <div ref={messagesEndRef} />
      </div>

      {!shouldAutoScroll && messages.length > 0 && (
        <button
          onClick={() => {
            setShouldAutoScroll(true);
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
          }}
          className="fixed bottom-24 right-8 p-2.5 bg-blue-600 text-white rounded-full shadow-lg hover:bg-blue-700 transition-colors z-10"
        >
          <ChevronDown className="h-5 w-5" />
        </button>
      )}
    </div>
  );
};
const MessageContent = ({ content }) => {
  const matches = linkifyInstance.match(content);
  if (!matches) return <span>{content}</span>;

  const elements = [];
  let lastIndex = 0;

  matches.forEach((match, i) => {
    // Add text before the link
    if (match.index > lastIndex) {
      elements.push(
        <span key={`text-${i}`}>
          {content.slice(lastIndex, match.index)}
        </span>
      );
    }

    // Add the link
    elements.push(
      <a
        key={`link-${i}`}
        href={match.url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 hover:underline"
      >
        {match.text}
      </a>
    );

    lastIndex = match.lastIndex;
  });

  // Add remaining text
  if (lastIndex < content.length) {
    elements.push(
      <span key="text-last">{content.slice(lastIndex)}</span>
    );
  }

  return <>{elements}</>;
};

const ChatMessage = ({
  message,
  onReply,
  onForward,
  isOwnMessage,
  showAvatar,
  isFirstInGroup,
  isLastInGroup
}) => {
  return (
    <div
      className={`
        group relative flex items-end space-x-2 px-4 py-0.5
        ${isOwnMessage ? 'flex-row-reverse space-x-reverse' : 'flex-row'}
      `}
    >
      {showAvatar && (
        <div className="relative flex-shrink-0 w-8">
          <div className="w-8 h-8 rounded-full overflow-hidden">
            {message.sender.avatar ? (
              <img
                src={message.sender.avatar}
                alt={message.sender.firstName}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className={`
                w-full h-full flex items-center justify-center
                ${isOwnMessage 
                  ? 'bg-blue-500'
                  : 'bg-gray-400'}
              `}>
                <span className="text-white text-sm font-medium">
                  {message.sender.firstName[0]}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      <div className={`
        flex flex-col max-w-[75%] sm:max-w-[65%]
        ${isOwnMessage ? 'items-end' : 'items-start'}
      `}>
        {isFirstInGroup && !isOwnMessage && (
          <span className="text-xs font-medium text-gray-500 ml-1 mb-1">
            {message.sender.firstName}
          </span>
        )}

        <div className={`
          relative group/message px-4 py-2 rounded-2xl
          ${isOwnMessage
            ? 'bg-blue-500 text-white ml-auto'
            : 'bg-gray-100 text-gray-900'}
        `}>
          <MessageContent content={message.content} />
          
          <div className={`
            flex items-center gap-1.5 mt-1 text-xs
            ${isOwnMessage ? 'text-blue-100' : 'text-gray-500'}
          `}>
            {format(new Date(message.createdAt), 'HH:mm')}
            {isOwnMessage && (
              message.isRead ? <CheckCheck className="h-3.5 w-3.5" /> : <Check className="h-3.5 w-3.5" />
            )}
          </div>
        </div>
      </div>

      <div className={`
        absolute top-1/2 -translate-y-1/2 opacity-0
        group-hover:opacity-100 transition-opacity
        ${isOwnMessage ? '-left-16' : '-right-16'}
      `}>
        <button
          onClick={() => onReply(message)}
          className="p-2 rounded-full bg-white shadow hover:bg-gray-50"
        >
          <Reply className="h-4 w-4 text-gray-600" />
        </button>
      </div>
    </div>
  );
};

// Message Group Component
const MessageGroup = ({ messages, onReply, onForward }) => {
  const currentUserId = localStorage.getItem("currentAlumniId");
  const [lastReadMessage, setLastReadMessage] = useState(null);

  useEffect(() => {
    const lastRead = messages
      .filter((m) => m.isRead && m.sender._id === currentUserId)
      .pop();
    setLastReadMessage(lastRead);
  }, [messages, currentUserId]);

  let lastSenderId = null;
  let lastDate = null;

  return (
    <div>
      {messages.map((message, index) => {
        const showDate =
          index === 0 ||
          !isSameDay(
            new Date(message.createdAt),
            new Date(messages[index - 1].createdAt)
          );

        // Determine if we should show the avatar
        const showAvatar = message.sender._id !== lastSenderId;
        lastSenderId = message.sender._id;

        return (
          <React.Fragment key={message._id}>
            {showDate && <DateSeparator date={new Date(message.createdAt)} />}
            <ChatMessage
              message={message}
              isOwnMessage={message.sender._id === currentUserId}
              onReply={onReply}
              onForward={onForward}
              showAvatar={showAvatar}
            />
          </React.Fragment>
        );
      })}
    </div>
  );
};

// Chat Sidebar Component
const ChatSidebar = ({
  connections,
  selectedConnection,
  onSelectConnection,
  searchTerm,
  onSearchChange,
  loading,
  onlineUsers,
}) => (
  <div className="w-80 flex flex-col bg-white border-r border-gray-100">
    <div className="p-4 border-b border-gray-100">
      <h2 className="text-xl font-semibold text-gray-900 mb-4">Messages</h2>
      <div className="relative">
        <input
          type="text"
          placeholder="Search conversations..."
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 bg-gray-50 rounded-xl text-sm 
            focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white
            transition-all"
        />
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
      </div>
    </div>

    {loading ? (
      <div className="flex-1 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent" />
      </div>
    ) : connections.length === 0 ? (
      <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
        <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mb-4">
          <User className="h-8 w-8 text-blue-500" />
        </div>
        <h3 className="font-medium text-gray-900 mb-1">No conversations yet</h3>
        <p className="text-sm text-gray-500">
          Connect with alumni to start chatting
        </p>
      </div>
    ) : (
      <div className="flex-1 overflow-y-auto">
        {connections.map((connection) => (
          <ConnectionListItem
            key={connection.connectionId}
            connection={connection}
            isSelected={selectedConnection?.connectionId === connection.connectionId}
            onClick={() => onSelectConnection(connection)}
            isOnline={onlineUsers?.has(connection.otherParty._id)}
          />
        ))}
      </div>
    )}
  </div>
);


// Connection List Item Component
const ConnectionListItem = ({ connection, isSelected, onClick, isOnline }) => (
  <button
    onClick={onClick}
    className={`
      w-full p-4 flex items-start space-x-3 transition-all
      ${
        isSelected
          ? "bg-blue-50/80 hover:bg-blue-50"
          : "hover:bg-gray-50"
      }
    `}
  >
    <div className="relative flex-shrink-0">
      {connection.otherParty.avatar ? (
        <img
          src={connection.otherParty.avatar}
          alt={`${connection.otherParty.firstName} ${connection.otherParty.lastName}`}
          className="h-12 w-12 rounded-full object-cover"
        />
      ) : (
        <div className="h-12 w-12 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center">
          <span className="text-lg font-medium text-white">
            {connection.otherParty.firstName[0]}
          </span>
        </div>
      )}
      <OnlineStatus isOnline={isOnline} />
      {connection.unreadCount > 0 && (
        <span className="absolute -top-1 -right-1 h-5 w-5 bg-blue-500 text-white text-xs font-medium flex items-center justify-center rounded-full">
          {connection.unreadCount}
        </span>
      )}
    </div>

    <div className="flex-1 min-w-0">
      <div className="flex justify-between items-start">
        <h4 className="font-medium text-gray-900 truncate">
          {connection.otherParty.firstName} {connection.otherParty.lastName}
        </h4>
        {connection.lastMessage && (
          <span className="text-xs text-gray-500 whitespace-nowrap ml-2">
            {format(new Date(connection.lastMessage.createdAt), "HH:mm")}
          </span>
        )}
      </div>
      
      <p className="text-sm text-gray-500 truncate mt-0.5">
        {connection.otherParty.department}
      </p>

      {connection.lastMessage && (
        <div className="flex items-center mt-1">
          {connection.lastMessage.sender._id === localStorage.getItem("currentAlumniId") && (
            <Check className="h-3.5 w-3.5 text-gray-400 mr-1" />
          )}
          <p className={`
            text-sm truncate
            ${connection.unreadCount > 0 ? "font-medium text-gray-900" : "text-gray-500"}
          `}>
            {connection.lastMessage.sender._id === connection.otherParty._id
              ? connection.lastMessage.content
              : `You: ${connection.lastMessage.content}`}
          </p>
        </div>
      )}
    </div>
  </button>
);

// Main ChatComponent
const ChatComponent = ({ initialConnectionId }) => {
  const { socket, onlineUsers, isUserOnline } = useChat();
  const [connections, setConnections] = useState([]);
  const [selectedConnection, setSelectedConnection] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [loadingConnections, setLoadingConnections] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [showMobileChat, setShowMobileChat] = useState(false);
  const typingTimeoutRef = useRef(null);
  const messagesEndRef = useRef(null);

  // Socket event handlers
  useEffect(() => {
    if (!socket) return;

    socket.on("new_message", handleNewMessage);
    socket.on("message_notification", handleMessageNotification);
    socket.on("user_typing", handleUserTyping);
    socket.on("messages_read", handleMessagesRead);
    socket.on("error", handleSocketError);

    return () => {
      socket.off("new_message");
      socket.off("message_notification");
      socket.off("user_typing");
      socket.off("messages_read");
      socket.off("error");
    };
  }, [socket]);

  useEffect(() => {
    if (!socket) return;

    socket.on("new_message", (message) => {
      console.log("Received new message:", message);
      if (selectedConnection?.connectionId === message.connection) {
        setMessages((prev) => [...prev, message]);
        scrollToBottom();

        // Mark as read if we're the receiver
        if (message.receiver === localStorage.getItem("currentAlumniId")) {
          socket.emit("mark_read", { connectionId: message.connection });
        }
      }
    });

    return () => {
      socket.off("new_message");
    };
  }, [socket, selectedConnection]);

  const updateConnectionWithNewMessage = (newMessage) => {
    setConnections((prevConnections) =>
      prevConnections.map((conn) => {
        if (conn.connectionId === newMessage.connection) {
          return {
            ...conn,
            lastMessage: {
              content: newMessage.content,
              createdAt: newMessage.createdAt,
              sender: newMessage.sender,
            },
            unreadCount: conn.unreadCount + 1,
          };
        }
        return conn;
      })
    );
  };

  // Handle new message from socket
  const handleNewMessage = (message) => {
    if (selectedConnection?.connectionId === message.connection) {
      setMessages((prev) => [...prev, message]);
      socket.emit("mark_read", { connectionId: message.connection });
      scrollToBottom();
    }
    updateConnectionWithNewMessage(message);
  };

  // Handle message notification
  const handleMessageNotification = ({ message, sender }) => {
    toast.custom((t) => (
      <div
        className={`
        ${t.visible ? "animate-enter" : "animate-leave"}
        max-w-md w-full bg-white shadow-lg rounded-lg pointer-events-auto flex
      `}
      >
        <div className="flex-1 p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              {sender.personalInfo?.avatar ? (
                <img
                  className="h-10 w-10 rounded-full"
                  src={sender.personalInfo.avatar}
                  alt={sender.firstName}
                />
              ) : (
                <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <User className="h-6 w-6 text-blue-600" />
                </div>
              )}
            </div>
            <div className="ml-3 flex-1">
              <p className="text-sm font-medium text-gray-900">
                {sender.firstName} {sender.lastName}
              </p>
              <p className="mt-1 text-sm text-gray-500 truncate">
                {message.content}
              </p>
            </div>
          </div>
        </div>
        <div className="flex border-l border-gray-200">
          <button
            onClick={() => {
              setSelectedConnection(
                connections.find((c) => c.connectionId === message.connection)
              );
              toast.dismiss(t.id);
            }}
            className="w-full border border-transparent rounded-none rounded-r-lg p-4 flex items-center justify-center text-sm font-medium text-blue-600 hover:text-blue-500"
          >
            View
          </button>
        </div>
      </div>
    ));
  };

  // Handle typing indicator
  const handleUserTyping = ({ userId, isTyping: typing }) => {
    if (selectedConnection?.otherParty._id === userId) {
      setIsTyping(typing);
    }
  };

  // Handle messages read status update
  const handleMessagesRead = ({ userId }) => {
    if (selectedConnection?.otherParty._id === userId) {
      setMessages((prev) =>
        prev.map((msg) => ({
          ...msg,
          isRead:
            msg.sender._id === localStorage.getItem("currentAlumniId")
              ? true
              : msg.isRead,
        }))
      );
    }
  };

  // Handle socket errors
  const handleSocketError = (error) => {
    toast.error(error.message);
  };

  // Initial data loading
  useEffect(() => {
    fetchConnections();
  }, []);

  useEffect(() => {
    if (initialConnectionId && connections.length > 0) {
      const connection = connections.find(
        (c) => c.connectionId === initialConnectionId
      );
      if (connection) {
        setSelectedConnection(connection);
      }
    }
  }, [initialConnectionId, connections]);

  useEffect(() => {
    if (selectedConnection) {
      fetchMessages();
      socket.emit("join_chat", selectedConnection.connectionId);
    }
    return () => {
      if (selectedConnection) {
        socket.emit("leave_chat", selectedConnection.connectionId);
      }
    };
  }, [selectedConnection]);

  // Fetch connections and messages
  const fetchConnections = async () => {
    try {
      setLoadingConnections(true);
      const [connectionsResponse, messagesResponse] = await Promise.all([
        fetch(`${process.env.REACT_APP_BASE_URL}/api/connections/accepted`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }),
        fetch(`${process.env.REACT_APP_BASE_URL}/api/messages/recent`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }),
      ]);

      if (!connectionsResponse.ok || !messagesResponse.ok) {
        throw new Error("Failed to fetch data");
      }

      const [connections, recentMessages] = await Promise.all([
        connectionsResponse.json(),
        messagesResponse.json(),
      ]);

      const enhancedConnections = connections.map((conn) => {
        const recentMessage = recentMessages.find(
          (msg) => msg.connectionId === conn._id
        );

        return {
          connectionId: conn._id,
          otherParty: {
            _id: conn._id,
            firstName: conn.firstName,
            lastName: conn.lastName,
            avatar: conn.personalInfo?.avatar,
            department: conn.department,
          },
          lastMessage: recentMessage?.lastMessage || null,
          unreadCount: recentMessage?.unreadCount || 0,
        };
      });

      setConnections(enhancedConnections);
    } catch (err) {
      toast.error("Failed to load connections");
      console.error("Error fetching connections:", err);
    } finally {
      setLoadingConnections(false);
    }
  };

  // Fetch messages
  const fetchMessages = async () => {
    if (!selectedConnection?.connectionId) return;

    try {
      setLoadingMessages(true);
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/messages/connection/${selectedConnection.connectionId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!response.ok) throw new Error("Failed to fetch messages");

      const data = await response.json();
      setMessages(data);
      scrollToBottom();

      // Mark messages as read
      if (selectedConnection.unreadCount > 0) {
        socket.emit("mark_read", {
          connectionId: selectedConnection.connectionId,
        });
      }
    } catch (err) {
      toast.error("Failed to load messages");
      console.error("Error fetching messages:", err);
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedConnection) return;

    const messageContent = newMessage.trim();
    setNewMessage(""); // Clear input immediately for better UX

    try {
      socket.emit("send_message", {
        connectionId: selectedConnection.connectionId,
        content: messageContent,
        receiverId: selectedConnection.otherParty._id,
        replyTo: replyTo?._id,
      });

      setReplyTo(null);
      setShowEmojiPicker(false);
    } catch (err) {
      console.error("Error sending message:", err);
      toast.error("Failed to send message");
      setNewMessage(messageContent); // Restore message if failed
    }
  };

  // Handle typing indicator
  const handleTyping = () => {
    socket.emit("typing", {
      connectionId: selectedConnection.connectionId,
      isTyping: true,
    });

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    typingTimeoutRef.current = setTimeout(() => {
      socket.emit("typing", {
        connectionId: selectedConnection.connectionId,
        isTyping: false,
      });
    }, 1000);
  };

  // Forward message handler
  const handleForward = async (message, targetConnectionId) => {
    try {
      socket.emit("send_message", {
        connectionId: targetConnectionId || selectedConnection.connectionId,
        content: message.content,
        receiverId: selectedConnection.otherParty._id,
        forwardedFrom: message._id,
      });

      toast.success("Message forwarded");
    } catch (err) {
      toast.error("Failed to forward message");
      console.error("Error forwarding message:", err);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const filteredConnections = connections.filter((conn) =>
    `${conn.otherParty.firstName} ${conn.otherParty.lastName}`
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  const handleSelectConnection = (connection) => {
    setSelectedConnection(connection);
    setShowMobileChat(true); // Show chat view on mobile when a connection is selected
  };

   // Handle back button in mobile view
   const handleMobileBack = () => {
    setShowMobileChat(false);
    // Optionally, you can also clear the selected connection
    // setSelectedConnection(null);
  };

  return (
    <div className="h-[100dvh] flex bg-white overflow-hidden">
      {/* Sidebar - Shown by default on mobile, always visible on desktop */}
      <div 
        className={`
          w-full md:w-80 flex-shrink-0 border-r border-gray-100
          ${showMobileChat ? 'hidden md:block' : 'block'}
        `}
      >
        <ChatSidebar
          connections={filteredConnections}
          selectedConnection={selectedConnection}
          onSelectConnection={handleSelectConnection}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          loading={loadingConnections}
          onlineUsers={onlineUsers}
        />
      </div>

      {/* Main Chat Area - Hidden by default on mobile, shown when chat is selected */}
      <div 
        className={`
          flex-1 flex flex-col h-full overflow-hidden
          ${showMobileChat ? 'block' : 'hidden md:block'}
        `}
      >
        {selectedConnection ? (
          <>
            <ChatHeader
              connection={selectedConnection}
              onBack={handleMobileBack}
              isOnline={isUserOnline(selectedConnection.otherParty._id)}
            />

            <div className="flex-1 overflow-y-auto bg-white">
              <MessageList
                messages={messages}
                onReply={setReplyTo}
                onForward={handleForward}
              />
            </div>

            <MessageInput
              value={newMessage}
              onChange={(e) => {
                setNewMessage(e.target.value);
                handleTyping();
              }}
              onSend={handleSendMessage}
              replyTo={replyTo}
              onCancelReply={() => setReplyTo(null)}
              showEmojiPicker={showEmojiPicker}
              onToggleEmojiPicker={() => setShowEmojiPicker(!showEmojiPicker)}
              onEmojiSelect={(emoji) => {
                setNewMessage(prev => prev + emoji.native);
                setShowEmojiPicker(false);
              }}
              onAttachmentClick={() => toast.error('Attachments coming soon!')}
              isRecording={false}
              onRecordStart={() => toast.error('Voice messages coming soon!')}
              onRecordStop={() => {}}
            />
          </>
        ) : (
          <div className="hidden md:flex flex-1 items-center justify-center bg-gray-50">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="h-8 w-8 text-blue-500" />
              </div>
              <h3 className="text-xl font-medium text-gray-900 mb-2">Select a conversation</h3>
              <p className="text-gray-500">Choose a connection to start chatting</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatComponent;